/**
 * mesh3d → 二進位（給大圖用的傳輸格式）。
 *
 * 為什麼需要它
 * ────────────
 * mesh3d 原本只有 JSON 一種形式，而 JSON 是**一條字串** —— V8 與 Chrome 的
 * 單一字串上限都是 ~512 MB（536,870,888 字元）。實測那張 38 MB 的 Revit
 * 「3D 視圖」完整內容換成 JSON 要 **669 MiB**，兩端都送不出去，只好在伺服器
 * 端取樣（只看得到一部分元件）。
 *
 * 二進位沒有這個限制：同樣的內容是 241 MB 的 typed array，瀏覽器直接拿去建
 * BufferGeometry，**不必解析文字、不必經過字串**。所以「能不能顯示完整圖面」
 * 從「固定 512 MB」變成「機器有多少記憶體」。
 *
 * 這個模組**同時給伺服器與瀏覽器用**（前端由 build.mjs 一起打包），所以只用
 * TypedArray 與 TextDecoder，不碰 Node 的 Buffer。
 *
 * 格式（全部 little-endian，各區塊 4-byte 對齊 → 前端可零複製取 view）
 * ─────────────────────────────────────────────────────────────────
 *   offset 0   4 B     magic "M3DB"
 *   offset 4   4 B     version (uint32)
 *   offset 8   4 B     headerLen (uint32)
 *   offset 12  n B     header：UTF-8 JSON，以空白補齊到 4 的倍數
 *   之後      12V B    positions  Float32[3V]
 *   之後      12T B    triIndex   Uint32[3T]   ← 3 個索引一個三角形
 *   之後       8L B    lineIndex  Uint32[2L]   ← 2 個索引一條線段
 *   之後       4P B    pointIndex Uint32[P]
 *
 * header 內容
 * ───────────
 *   {
 *     version: 1,
 *     bbox: { min:[x,y,z], max:[x,y,z] },
 *     counts: { verts, tris, lines, points },
 *     stats: { ...mesh.stats },
 *     groups: {
 *       face:  [ { color, start, count } ],   // start/count 以「索引」為單位
 *       line:  [ ... ],
 *       point: [ ... ],
 *     }
 *   }
 *
 * 為什麼要 groups：顏色是「一個元件一個顏色」，不是逐頂點。這個檔只有 14 種
 * 顏色（11 面色 + 3 線色），所以把索引依顏色排好、用 `addGroup()` + 材質陣列，
 * 只要十幾個 draw call，**完全不需要 per-vertex 顏色緩衝**（省下 180 MB 以上）。
 */

export const MESH_BIN_MAGIC = 'M3DB';
export const MESH_BIN_VERSION = 1;

/** header + magic 的保守保留量（實際 header 只有幾百 bytes）。 */
const HEADER_RESERVE = 4096;

/** 從「數量」直接算二進位大小（不必先建內容）—— 給取樣決策用。 */
export function binaryBytesFromCounts({ verts = 0, triIndices = 0, lineIndices = 0, pointIndices = 0 }) {
  return HEADER_RESERVE + verts * 12 + triIndices * 4 + lineIndices * 4 + pointIndices * 4;
}

/** 一個實際 mesh 物件的二進位大小（quad 會展開成 2 個三角形）。 */
export function mesh3dBinaryBytes(mesh) {
  return binaryBytesFromCounts({
    verts: (mesh?.pos?.length ?? 0) / 3,
    triIndices: (mesh?.tris?.length ?? 0) + (mesh?.quads?.length ?? 0) / 4 * 6,
    lineIndices: mesh?.lines?.length ?? 0,
    pointIndices: mesh?.points?.length ?? 0,
  });
}

/**
 * 估算 mesh3d 的 JSON 大小（保守）。
 *
 * 給「要不要留一份 .json 後備」的判斷用。gltf-to-mesh3d 會在 stats 裡回報更準的
 * `estJsonBytes`；這裡是給沒有那個欄位的來源（例如 LibreDWG 那條路，或舊資料）
 * 用的，所以用**全精度**的係數（寧可高估 → 不留 JSON，也不要低估而寫出一份
 * 超過 512 MB 字串上限的檔案）。
 */
export function estimateMeshJsonBytes(mesh) {
  const verts = (mesh?.pos?.length ?? 0) / 3;
  const indexEntries = (mesh?.tris?.length ?? 0) + (mesh?.quads?.length ?? 0) / 4 * 6
    + (mesh?.lines?.length ?? 0) + (mesh?.points?.length ?? 0);
  const colors = (mesh?.trisColor?.length ?? 0) + (mesh?.quadsColor?.length ?? 0) * 2
    + (mesh?.linesColor?.length ?? 0) + (mesh?.pointsColor?.length ?? 0);
  return verts * 3 * 20 + indexEntries * 7 + colors * 9;
}

/**
 * 把索引依顏色分群（counting sort，O(n)）。
 *
 * @param {ArrayLike<number>} indices  攤平的索引（每個 primitive 佔 perPrim 個）
 * @param {number} perPrim             3 = 三角形、2 = 線段、1 = 點
 * @param {ArrayLike<number>} colors   每個 primitive 一個顏色（0xRRGGBB 整數）
 */
function groupByColor(indices, perPrim, colors) {
  const n = colors.length;
  const counts = new Map();
  for (let i = 0; i < n; i++) {
    const c = colors[i] ?? 0;
    counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  const groups = [];
  const cursor = new Map();
  let prim = 0;
  for (const [color, cnt] of counts) {
    groups.push({ color, start: prim * perPrim, count: cnt * perPrim });
    cursor.set(color, prim * perPrim);
    prim += cnt;
  }
  const out = new Uint32Array(indices.length);
  for (let i = 0; i < n; i++) {
    const c = colors[i] ?? 0;
    const p = cursor.get(c);
    const src = i * perPrim;
    for (let k = 0; k < perPrim; k++) out[p + k] = indices[src + k];
    cursor.set(c, p + perPrim);
  }
  return { out, groups };
}

/** quad（4 個索引）展開成 2 個三角形（6 個索引），顏色每個 quad 一份。 */
function expandQuads(quads, quadsColor) {
  const n = Math.floor(quads.length / 4);
  if (!n) return { indices: new Uint32Array(0), colors: [] };
  const indices = new Uint32Array(n * 6);
  const colors = new Array(n * 2);
  for (let q = 0; q < n; q++) {
    const a = quads[q * 4];
    const b = quads[q * 4 + 1];
    const c = quads[q * 4 + 2];
    const d = quads[q * 4 + 3];
    indices[q * 6] = a; indices[q * 6 + 1] = b; indices[q * 6 + 2] = c;
    indices[q * 6 + 3] = a; indices[q * 6 + 4] = c; indices[q * 6 + 5] = d;
    const col = quadsColor?.[q] ?? 0xbfc8d8;
    colors[q * 2] = col;
    colors[q * 2 + 1] = col;
  }
  return { indices, colors };
}

const pad4 = (n) => (n + 3) & ~3;

/**
 * 把 mesh3d 物件序列化成 Uint8Array（Node 可直接寫檔，瀏覽器可直接用）。
 *
 * @param {object} mesh
 * @returns {Uint8Array}
 */
export function mesh3dToBinary(mesh) {
  const pos = mesh?.pos ?? [];
  const verts = Math.floor(pos.length / 3);

  // 三角形 = tris + 展開後的 quads
  const quad = expandQuads(mesh?.quads ?? [], mesh?.quadsColor ?? []);
  const triIn = new Uint32Array((mesh?.tris?.length ?? 0) + quad.indices.length);
  triIn.set(mesh?.tris ?? [], 0);
  triIn.set(quad.indices, mesh?.tris?.length ?? 0);
  const triColors = [...(mesh?.trisColor ?? []), ...quad.colors];

  const face = groupByColor(triIn, 3, triColors);
  const line = groupByColor(mesh?.lines ?? [], 2, mesh?.linesColor ?? []);
  const point = groupByColor(mesh?.points ?? [], 1, mesh?.pointsColor ?? []);

  const header = {
    version: MESH_BIN_VERSION,
    bbox: mesh?.bbox ?? { min: [0, 0, 0], max: [0, 0, 0] },
    counts: {
      verts,
      tris: face.groups.reduce((s, g) => s + g.count, 0) / 3,
      lines: line.groups.reduce((s, g) => s + g.count, 0) / 2,
      points: point.groups.reduce((s, g) => s + g.count, 0),
    },
    stats: mesh?.stats ?? {},
    ...(mesh?.objects?.length ? {objects:mesh.objects} : {}),
    groups: { face: face.groups, line: line.groups, point: point.groups },
  };
  const headerRaw = new TextEncoder().encode(JSON.stringify(header));
  const headerLen = pad4(headerRaw.length);

  const positions = new Float32Array(verts * 3);
  for (let i = 0, n = verts * 3; i < n; i++) positions[i] = pos[i] ?? 0;

  const total = 12 + headerLen + positions.byteLength
    + face.out.byteLength + line.out.byteLength + point.out.byteLength;
  const out = new Uint8Array(total);
  const view = new DataView(out.buffer);
  out[0] = 0x4d; out[1] = 0x33; out[2] = 0x44; out[3] = 0x42;   // 'M3DB'
  view.setUint32(4, MESH_BIN_VERSION, true);
  view.setUint32(8, headerLen, true);
  out.set(headerRaw, 12);
  out.fill(0x20, 12 + headerRaw.length, 12 + headerLen);        // 空白補齊
  let off = 12 + headerLen;
  out.set(new Uint8Array(positions.buffer, positions.byteOffset, positions.byteLength), off);
  off += positions.byteLength;
  out.set(new Uint8Array(face.out.buffer, face.out.byteOffset, face.out.byteLength), off);
  off += face.out.byteLength;
  out.set(new Uint8Array(line.out.buffer, line.out.byteOffset, line.out.byteLength), off);
  off += line.out.byteLength;
  out.set(new Uint8Array(point.out.buffer, point.out.byteOffset, point.out.byteLength), off);
  return out;
}

/** 各區塊在 buffer 中的 byteOffset（前端零複製取 view 用）。 */
export function meshBinaryOffsets(headerLen, header) {
  const c = header?.counts ?? {};
  const positions = 12 + headerLen;
  const triIndex = positions + (c.verts ?? 0) * 12;
  const lineIndex = triIndex + (c.tris ?? 0) * 12;
  const pointIndex = lineIndex + (c.lines ?? 0) * 8;
  return { positions, triIndex, lineIndex, pointIndex };
}

/**
 * 只讀 header（不碰幾何資料）+ 算出各區塊位移。
 *
 * @param {ArrayBuffer|Uint8Array} input
 */
export function parseMeshBinary(input) {
  const buf = input instanceof Uint8Array ? input : new Uint8Array(input);
  if (buf.byteLength < 12) throw new Error('mesh bin 太短');
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const magic = String.fromCharCode(buf[0], buf[1], buf[2], buf[3]);
  if (magic !== MESH_BIN_MAGIC) throw new Error(`mesh bin magic 不對：${magic}`);
  const version = view.getUint32(4, true);
  const headerLen = view.getUint32(8, true);
  const raw = new TextDecoder().decode(buf.subarray(12, 12 + headerLen)).trim();
  const header = JSON.parse(raw);
  return { version, header, headerLen, offsets: meshBinaryOffsets(headerLen, header), buf };
}

/** 從 header 的 counts 估算 JSON 大小（保守）—— 決定「要不要也留一份 .json」。 */
export function estimateJsonBytesFromCounts({ verts = 0, triIndices = 0, lineIndices = 0, pointIndices = 0 }) {
  const indexEntries = triIndices + lineIndices + pointIndices;
  const colors = triIndices / 3 + lineIndices / 2 + pointIndices;
  return verts * 3 * 20 + indexEntries * 7 + colors * 9;
}

/**
 * 二進位還原成 mesh3d 物件。
 *
 * 只有一個用途：**舊的 JSON 後備產物**。當轉檔命中快取時手上只有二進位，
 * 但小圖仍然應該留一份 `.json`（舊客戶端、curl 除錯、以及 UI 測試用來判斷
 * 「這個檔能不能用」）。大圖不會走到這裡 —— 呼叫端會先用
 * `estimateJsonBytesFromCounts()` 判斷超不超過預算。
 *
 * @param {ArrayBuffer|Uint8Array} input
 * @returns {object} mesh3d
 */
export function mesh3dFromBinary(input) {
  const { header, offsets, buf } = parseMeshBinary(input);
  const c = header.counts;
  const base = buf.byteOffset;
  const pos = Array.from(new Float32Array(buf.buffer, base + offsets.positions, c.verts * 3));

  const tris = [];
  const trisColor = [];
  for (const g of header.groups?.face ?? []) {
    const idx = new Uint32Array(buf.buffer, base + offsets.triIndex + g.start * 4, g.count);
    for (let i = 0; i + 2 < idx.length; i += 3) {
      tris.push(idx[i], idx[i + 1], idx[i + 2]);
      trisColor.push(g.color);
    }
  }
  const lines = [];
  const linesColor = [];
  for (const g of header.groups?.line ?? []) {
    const idx = new Uint32Array(buf.buffer, base + offsets.lineIndex + g.start * 4, g.count);
    for (let i = 0; i + 1 < idx.length; i += 2) {
      lines.push(idx[i], idx[i + 1]);
      linesColor.push(g.color);
    }
  }
  const points = [];
  const pointsColor = [];
  for (const g of header.groups?.point ?? []) {
    const idx = new Uint32Array(buf.buffer, base + offsets.pointIndex + g.start * 4, g.count);
    for (let i = 0; i < idx.length; i++) {
      points.push(idx[i]);
      pointsColor.push(g.color);
    }
  }
  return {
    pos, quads: [], tris, lines, points,
    quadsColor: [], trisColor, linesColor, pointsColor,
    bbox: header.bbox, stats: header.stats ?? {},
    ...(header.objects?.length ? {objects:header.objects}: {}),
  };
}
